import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Card, ConfirmDialog, Field, Input } from "../../shared/ui/index.js";
import { fromDateTimeLocal, toDateTimeLocal } from "../../shared/format.js";
import { useToast } from "../../shared/toast.js";
import { useDeleteEvent, useUpdateEvent, type EventPatch } from "./events.api.js";
import type { EventRow } from "../../shared/types.js";

export function EventSettingsPanel({ event }: { event: EventRow }) {
  const update = useUpdateEvent(event.id);
  const del = useDeleteEvent(event.id, event.weddingId);
  const navigate = useNavigate();
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(false);

  const [name, setName] = useState(event.name);
  const [date, setDate] = useState(toDateTimeLocal(event.date));
  const [location, setLocation] = useState(event.location ?? "");
  const [start, setStart] = useState(toDateTimeLocal(event.captureStartAt));
  const [end, setEnd] = useState(toDateTimeLocal(event.captureEndAt));
  const [reveal, setReveal] = useState(toDateTimeLocal(event.revealAt));

  const windowInvalid = start && end && new Date(start) >= new Date(end);

  function save() {
    const patch: EventPatch = {
      name,
      date: fromDateTimeLocal(date),
      location,
      revealAt: fromDateTimeLocal(reveal),
    };
    if (start) patch.captureStartAt = fromDateTimeLocal(start);
    if (end) patch.captureEndAt = fromDateTimeLocal(end);
    update.mutate(patch, {
      onSuccess: () => toast("Événement mis à jour"),
      onError: () => toast("Vérifiez les dates", "err"),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h2 className="font-serif text-lg">Détails</h2>
        <div className="mt-3 flex flex-col gap-3">
          <Field label="Nom">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Field label="Date & heure">
              <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Lieu">
              <Input value={location} onChange={(e) => setLocation(e.target.value)} />
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="font-serif text-lg">Fenêtre de contribution</h2>
        <p className="mt-1 text-sm text-stone-500">
          Les invités ne peuvent envoyer photos et vidéos qu'entre ces deux moments.
        </p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <Field label="Ouverture">
            <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Fermeture">
            <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        {windowInvalid && (
          <p className="mt-1 text-xs text-rose-600">La fermeture doit être après l'ouverture.</p>
        )}
      </Card>

      <Card>
        <h2 className="font-serif text-lg">Révélation de la galerie</h2>
        <p className="mt-1 text-sm text-stone-500">
          Avant ce moment, les invités ne voient pas les contributions (surprise).
        </p>
        <Field label="Ouverture de la galerie">
          <Input
            type="datetime-local"
            value={reveal}
            onChange={(e) => setReveal(e.target.value)}
            className="mt-3"
          />
        </Field>
      </Card>

      <Card>
        <h2 className="font-serif text-lg">Mur des invités</h2>
        <p className="mt-1 text-sm text-stone-500">
          Un fil style réseau social dans l'appli invité : chaque photo/vidéo publiée
          apparaît en direct avec l'auteur, et les invités peuvent liker.
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={event.wallEnabled}
            disabled={update.isPending}
            onChange={(e) => {
              const next = e.target.checked;
              update.mutate(
                { wallEnabled: next },
                { onSuccess: () => toast(next ? "Mur activé" : "Mur désactivé") },
              );
            }}
          />
          Activer le mur des invités
        </label>
        <p className="mt-2 text-xs text-stone-400">
          Attention : le mur montre les médias en temps réel, sans attendre la
          révélation de la galerie.
        </p>
      </Card>

      <div>
        <Button onClick={save} disabled={update.isPending || !!windowInvalid}>
          {update.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      <Card className="border-rose-200 bg-rose-50/60">
        <h2 className="font-serif text-lg text-rose-900">Supprimer cet événement</h2>
        <p className="mt-1 text-sm text-rose-700">
          Efface l'événement, son QR code et tous les médias déposés par les invités.
        </p>
        <Button variant="danger" className="mt-4" onClick={() => setConfirmDel(true)}>
          Supprimer
        </Button>
      </Card>

      {confirmDel && (
        <ConfirmDialog
          title="Supprimer cet événement ?"
          body={`"${event.name}", son QR code et tous les médias seront définitivement supprimés.`}
          confirmLabel="Supprimer définitivement"
          onConfirm={() =>
            del.mutate(undefined, {
              onSuccess: () =>
                navigate({ to: "/app/weddings/$id", params: { id: event.weddingId } }),
            })
          }
          onClose={() => setConfirmDel(false)}
        />
      )}
    </div>
  );
}
